import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Calendar, Users, X, Loader2 } from 'lucide-react';
import { decodeHtmlEntities } from '../../../utils/textUtils';
import { getTripStatusInfo } from '../../../config/statusConfig';

/**
 * GlobalSearch — Topbar search across trips and clients.
 *
 * Uses GET /api/search?q= backend endpoint for server-side search.
 * Falls back to client-side filtering if the endpoint isn't available.
 * Keyboard shortcut: Cmd/Ctrl + K to focus.
 */
export default function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState({ trips: [], clients: [] });
  const [searching, setSearching] = useState(false);
  const [useBackend, setUseBackend] = useState(true);
  const [fallbackData, setFallbackData] = useState(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Keyboard shortcut: Cmd/Ctrl + K to focus, Esc to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Load fallback data for client-side search
  const loadFallbackData = useCallback(async () => {
    if (fallbackData) return;
    try {
      const [tripsRes, clientsRes] = await Promise.all([
        fetch(`${import.meta.env.VITE_API_URL}/api/trips`, { credentials: 'include' }),
        fetch(`${import.meta.env.VITE_API_URL}/api/clients`, { credentials: 'include' }),
      ]);
      const trips = tripsRes.ok ? await tripsRes.json() : [];
      const clients = clientsRes.ok ? await clientsRes.json() : [];
      setFallbackData({
        trips: Array.isArray(trips) ? trips : Array.isArray(trips?.data) ? trips.data : [],
        clients: Array.isArray(clients) ? clients : Array.isArray(clients?.data) ? clients.data : [],
      });
    } catch { /* silent */ }
  }, [fallbackData]);

  // Debounced search
  useEffect(() => {
    if (query.trim().length < 2) {
      setResults({ trips: [], clients: [] });
      setSearching(false);
      return;
    }

    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);

      if (useBackend) {
        try {
          const res = await fetch(
            `${import.meta.env.VITE_API_URL}/api/search?q=${encodeURIComponent(query.trim())}&limit=5`,
            { credentials: 'include' },
          );
          if (res.ok) {
            const data = await res.json();
            const payload = data?.data || data;
            setResults({
              trips: payload.trips || [],
              clients: payload.clients || [],
            });
            setSearching(false);
            return;
          }
          // Backend endpoint not available — fall back to client-side
          setUseBackend(false);
        } catch {
          setUseBackend(false);
        }
      }

      // Client-side fallback
      if (!fallbackData) await loadFallbackData();
      const q = query.toLowerCase().trim();
      const fd = fallbackData || { trips: [], clients: [] };
      setResults({
        trips: fd.trips.filter(t =>
          (t.tripname || '').toLowerCase().includes(q) ||
          (t.fullname || '').toLowerCase().includes(q)
        ).slice(0, 5),
        clients: fd.clients.filter(c =>
          (c.fullname || c.FullName || '').toLowerCase().includes(q) ||
          (c.email || '').toLowerCase().includes(q)
        ).slice(0, 5),
      });
      setSearching(false);
    }, 250);

    return () => clearTimeout(debounceRef.current);
  }, [query, useBackend, fallbackData, loadFallbackData]);

  const hasResults = results.trips.length > 0 || results.clients.length > 0;
  const showDropdown = open && query.trim().length >= 2;

  const handleSelect = (type, item) => {
    setOpen(false);
    setQuery('');
    if (type === 'trip') {
      navigate('/agent/crm/trips');
    } else if (type === 'client') {
      navigate(`/agent/crm/clients/${item.clientid}`);
    }
  };

  return (
    <div ref={containerRef} className="hidden relative md:block">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 -translate-y-1/2" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search trips & clients..."
          className="py-2 pr-16 pl-9 w-64 text-sm text-white placeholder-gray-400 bg-gray-700/60 rounded-lg border border-gray-600 transition-all focus:w-80 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 lg:w-72"
        />
        <kbd className="absolute right-3 top-1/2 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 bg-gray-600/50 rounded border border-gray-500 -translate-y-1/2">
          {navigator.platform?.includes('Mac') ? '⌘' : 'Ctrl'}K
        </kbd>
      </div>

      {/* Results dropdown */}
      {showDropdown && (
        <div className="absolute left-0 top-full z-dropdown mt-2 w-96 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden">
          {searching ? (
            <div className="flex gap-2 justify-center items-center px-4 py-6 text-sm text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching...
            </div>
          ) : !hasResults ? (
            <div className="px-4 py-6 text-sm text-center text-gray-500">
              No results for "{query.trim()}"
            </div>
          ) : (
            <div className="overflow-y-auto max-h-80">
              {/* Trip results */}
              {results.trips.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-[10px] font-semibold tracking-wider text-gray-400 uppercase bg-gray-50 border-b border-gray-100">
                    Trips
                  </div>
                  {results.trips.map((trip) => {
                    const si = getTripStatusInfo(trip.status);
                    return (
                      <button
                        key={trip.tripid}
                        type="button"
                        onClick={() => handleSelect('trip', trip)}
                        className="flex gap-3 items-center px-3 py-2.5 w-full text-left transition-colors hover:bg-gray-50"
                      >
                        <Calendar className="flex-shrink-0 w-4 h-4 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {decodeHtmlEntities(trip.tripname) || 'Unnamed trip'}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {trip.fullname || trip.clientemail || ''}
                          </p>
                        </div>
                        <span className={`flex-shrink-0 px-2 py-0.5 text-[10px] font-medium rounded-full border ${si.color}`}>
                          {si.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Client results */}
              {results.clients.length > 0 && (
                <div>
                  <div className="px-3 py-2 text-[10px] font-semibold tracking-wider text-gray-400 uppercase bg-gray-50 border-b border-gray-100">
                    Clients
                  </div>
                  {results.clients.map((client) => (
                    <button
                      key={client.clientid}
                      type="button"
                      onClick={() => handleSelect('client', client)}
                      className="flex gap-3 items-center px-3 py-2.5 w-full text-left transition-colors hover:bg-gray-50"
                    >
                      <Users className="flex-shrink-0 w-4 h-4 text-gray-400" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {client.fullname || client.FullName || 'Unknown'}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {client.email || ''}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
